import BaseService, { type QueryParams } from '@/services/BaseService';
import { ResponseError } from '@/lib/exceptions';
import type { RolePermissionModel } from '@/types/admin/permission';
import { ADMIN_ROLES_PATH, AUTHORIZATION_PROXY_BASE } from './constants';

const ADMIN_ROLE_PERMISSIONS_PATH = 'admin/role-permissions';

/**
 * Admin API for role ↔ permission assignments.
 *
 * - Per-role: `admin/roles/{roleId}/permissions`
 * - Bulk:    `admin/role-permissions?roleIds=1,2,3`
 */
export class AdminRolePermissionService extends BaseService {
  constructor() {
    super(AUTHORIZATION_PROXY_BASE);
  }

  async listForRole(roleId: number): Promise<RolePermissionModel[]> {
    return this.get<RolePermissionModel[]>({
      path: `${ADMIN_ROLES_PATH}/${roleId}/permissions`,
    });
  }

  /** Bulk fetch: returns all role-permission assignments in a single request. */
  async listAll(): Promise<RolePermissionModel[]> {
    const result = await this.get<RolePermissionModel[]>({
      path: ADMIN_ROLE_PERMISSIONS_PATH,
    });

    if (!Array.isArray(result)) {
      console.warn('[AdminRolePermissionService] listAll() did not return an array, got:', typeof result);
      return [];
    }
    return result;
  }

  /** Bulk fetch: returns assignments for specific role IDs in a single request. */
  async listForRoles(roleIds: number[]): Promise<RolePermissionModel[]> {
    if (roleIds.length === 0) {
      return [];
    }
    return this.get<RolePermissionModel[]>({
      path: ADMIN_ROLE_PERMISSIONS_PATH,
      params: { roleIds } as QueryParams,
    });
  }

  async upsertAssignment(
    roleId: number,
    body: { permissionId: number; effect: string },
  ): Promise<RolePermissionModel | void> {
    return this.put<{ effect: string }, RolePermissionModel | void>({
      path: `${ADMIN_ROLES_PATH}/${roleId}/permissions/${body.permissionId}`,
      body: { effect: body.effect },
    });
  }

  async removeAssignment(roleId: number, permissionId: number): Promise<void> {
    try {
      await this.delete<void>({
        path: `${ADMIN_ROLES_PATH}/${roleId}/permissions/${permissionId}`,
      });
    } catch (e: unknown) {
      // Idempotent revoke: list/matrix can be ahead of server or row already removed.
      if (e instanceof ResponseError && e.statusCode === 404) {
        return;
      }
      throw e;
    }
  }
}
